import { useNavigateBack } from '@iheartjane/menu';
import {
  Button,
  Card,
  ChevronLeftIcon,
  EditIcon,
  Flex,
  Loading,
  Modal,
  TrashIcon,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import { useCallback, useState } from 'react';

import {
  useCreateCloudflareIntegration,
  useDeactivateCloudflareIntegration,
  useDeleteCloudflareIntegration,
  useDeployCloudflareIntegration,
  useFetchCloudflareIntegrations,
  useFetchCurrentBloomMenu,
  useUpdateCloudflareIntegration,
} from '@jane/business-admin/data-access';
import type { PartnerCloudflareIntegration } from '@jane/business-admin/types';
import { businessPaths } from '@jane/business-admin/util';
import { ConfirmChangeModal } from '@jane/shared-b2b/components';
import { ApiRequestError } from '@jane/shared/data-access';

import type { CloudflareIntegrationSubmitData } from './components/CloudflareIntegrationModal';
import { CloudflareIntegrationModal } from './components/CloudflareIntegrationModal';

import styles from './premiumCloudflareIntegrations.module.css';

const IntegrationItem = ({
  integration,
  menuId,
  onEdit,
}: {
  integration: PartnerCloudflareIntegration;
  menuId?: number;
  onEdit: (integration: PartnerCloudflareIntegration) => void;
}) => {
  const toast = useToast();
  const [confirmAction, setConfirmAction] = useState<
    'deploy' | 'deactivate' | 'delete' | null
  >(null);

  const { mutateAsync: deploy, isPending: isDeploying } =
    useDeployCloudflareIntegration({ integrationId: integration.id, menuId });
  const { mutateAsync: deactivate, isPending: isDeactivating } =
    useDeactivateCloudflareIntegration({
      integrationId: integration.id,
      menuId,
    });
  const { mutateAsync: deleteIntegration, isPending: isDeleting } =
    useDeleteCloudflareIntegration({ integrationId: integration.id, menuId });

  const canDeploy =
    integration.status === 'pending' ||
    integration.status === 'error' ||
    integration.status === 'deprovisioned';
  const canDeactivate = integration.status === 'provisioned';
  const canDelete =
    integration.status !== 'provisioned' &&
    integration.status !== 'provisioning';
  const isActionInProgress = isDeploying || isDeactivating || isDeleting;

  const handleConfirm = async () => {
    try {
      if (confirmAction === 'deploy') {
        await deploy();
        toast.add({ label: 'Integration deployed!', variant: 'success' });
      } else if (confirmAction === 'deactivate') {
        await deactivate();
        toast.add({ label: 'Integration deactivated.', variant: 'success' });
      } else if (confirmAction === 'delete') {
        await deleteIntegration();
        toast.add({ label: 'Integration deleted.', variant: 'success' });
      }
    } catch {
      toast.add({ label: 'An error occurred.', variant: 'error' });
    } finally {
      setConfirmAction(null);
    }
  };

  return (
    <>
      <Flex
        flexDirection="column"
        gap={8}
        border="grays-light"
        borderWidth="1px 0 0 0"
        px={24}
        py={24}
      >
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="body-bold">
            {integration.name || `Integration #${integration.id}`}
          </Typography>
          <Flex gap={8}>
            {canDeploy && (
              <Button
                label="Deploy"
                variant="primary"
                onClick={() => setConfirmAction('deploy')}
                loading={isDeploying}
                disabled={isActionInProgress}
              />
            )}
            {canDeactivate && (
              <Button
                label="Deactivate"
                variant="destructive"
                onClick={() => setConfirmAction('deactivate')}
                loading={isDeactivating}
                disabled={isActionInProgress}
              />
            )}
            <Button.Icon
              label="Edit"
              icon={<EditIcon />}
              variant="secondary"
              onClick={() => onEdit(integration)}
            />
            {canDelete && (
              <Button.Icon
                label="Delete"
                icon={<TrashIcon />}
                variant="destructive"
                onClick={() => setConfirmAction('delete')}
                disabled={isActionInProgress}
              />
            )}
          </Flex>
        </Flex>

        <Typography variant="caps" color="grays-mid">
          Route pattern
        </Typography>
        <Typography>{integration.route_pattern}</Typography>

        <Typography variant="caps" color="grays-mid" mt={8}>
          Account ID
        </Typography>
        <Typography>{integration.account_id?.slice(0, 8)}...</Typography>

        <Typography variant="caps" color="grays-mid" mt={8}>
          Status
        </Typography>
        <Typography>{integration.status}</Typography>
      </Flex>

      {isActionInProgress && confirmAction !== null ? (
        <Modal
          appId="parent"
          open
          onRequestClose={() => {
            /* prevent closing during action */
          }}
          variant="dialogue"
        >
          <Modal.Content>
            <Flex
              minWidth="432px"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={16}
              py={24}
            >
              <div style={{ position: 'relative', width: 48, height: 24 }}>
                <Loading color="black" />
              </div>
              <Typography variant="body-bold">
                {confirmAction === 'deploy'
                  ? 'Deploying... This may take a few seconds.'
                  : confirmAction === 'deactivate'
                    ? 'Deactivating... This may take a few seconds.'
                    : 'Deleting...'}
              </Typography>
            </Flex>
          </Modal.Content>
        </Modal>
      ) : (
        <ConfirmChangeModal
          open={confirmAction !== null}
          title={
            confirmAction === 'deploy'
              ? 'Deploy integration?'
              : confirmAction === 'deactivate'
                ? 'Deactivate integration?'
                : 'Delete integration?'
          }
          subtitle={
            confirmAction === 'deploy'
              ? 'This will deploy the Cloudflare Worker to your account and start routing traffic.'
              : confirmAction === 'deactivate'
                ? 'This will remove the Cloudflare Worker and routes from your account. Traffic will no longer be routed.'
                : 'This will permanently delete this integration record. This action cannot be undone.'
          }
          onConfirm={handleConfirm}
          onCancel={() => setConfirmAction(null)}
          confirmButtonVariant={
            confirmAction === 'deploy' ? undefined : 'destructive'
          }
          confirmLabel={
            confirmAction === 'deploy'
              ? 'Yes, deploy'
              : confirmAction === 'deactivate'
                ? 'Yes, deactivate'
                : 'Yes, delete'
          }
          cancelLabel="Cancel"
          minWidth="432px"
        />
      )}
    </>
  );
};

export const PremiumCloudflareIntegrations = () => {
  const toast = useToast();
  const goBack = useNavigateBack(businessPaths.premium());

  const bloomMenuQuery = useFetchCurrentBloomMenu();
  const menuId = bloomMenuQuery.data?.id;
  const integrationsQuery = useFetchCloudflareIntegrations(menuId);
  const integrations = integrationsQuery.data ?? [];

  const { mutateAsync: createIntegration } =
    useCreateCloudflareIntegration(menuId);
  const [editingIntegration, setEditingIntegration] =
    useState<PartnerCloudflareIntegration | null>(null);
  const { mutateAsync: updateIntegration } = useUpdateCloudflareIntegration({
    integrationId: editingIntegration?.id,
    menuId,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const handleAdd = useCallback(() => {
    setEditingIntegration(null);
    setServerError(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback(
    (integration: PartnerCloudflareIntegration) => {
      setEditingIntegration(integration);
      setServerError(null);
      setIsModalOpen(true);
    },
    []
  );

  const handleModalSubmit = useCallback(
    async (data: CloudflareIntegrationSubmitData) => {
      setServerError(null);
      try {
        if (editingIntegration) {
          await updateIntegration(data);
          toast.add({
            label: 'Integration updated!',
            variant: 'success',
          });
        } else {
          await createIntegration({
            api_token: data.api_token!,
            account_id: data.account_id,
            zone_id: data.zone_id,
            name: data.name || undefined,
            route_pattern: data.route_pattern || undefined,
            deploy_robots_txt: data.deploy_robots_txt,
          });
          toast.add({
            label: 'Integration created!',
            variant: 'success',
          });
        }
        setIsModalOpen(false);
      } catch (err: unknown) {
        if (err instanceof ApiRequestError) {
          try {
            const body = await err.response.json();
            const errorMessage =
              body?.errors?.validations?.api_token ||
              body?.errors?.error ||
              'An error occurred while saving.';
            setServerError(errorMessage);
          } catch {
            setServerError('An error occurred while saving.');
          }
        } else {
          toast.add({
            label: 'An error occurred.',
            variant: 'error',
          });
        }
      }
    },
    [editingIntegration, createIntegration, updateIntegration, toast]
  );

  return (
    <Flex flexDirection="column" height="100%" overflow="auto">
      <Flex
        className={styles['container']}
        pt={40}
        pb={40}
        width="848px"
        mx="auto"
        flexDirection="column"
      >
        <Flex flexDirection="column" mx={24}>
          <Flex
            gap={16}
            flexDirection="row"
            alignItems="center"
            alignContent="center"
          >
            <ChevronLeftIcon onClick={() => goBack()} altText="Go back" />
            <Typography variant="header-bold" branded>
              Cloudflare Integrations
            </Typography>
          </Flex>
        </Flex>

        <Card border="none" mt={24} mx={24}>
          <Card.Content p={24}>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              gap={24}
              mb={16}
            >
              <Typography variant="body-bold">Integrations</Typography>
              <Button
                label="Add integration"
                variant="secondary"
                onClick={handleAdd}
              />
            </Flex>
            <Typography color="grays-mid">
              Connect your Cloudflare account to serve your menu directly from
              your own domain. Each integration links a Cloudflare Worker to a
              URL pattern on your website, so visitors see your menu without
              leaving your site.
            </Typography>
          </Card.Content>

          {integrations.length > 0 &&
            integrations.map((integration) => (
              <IntegrationItem
                key={integration.id}
                integration={integration}
                menuId={menuId}
                onEdit={handleEdit}
              />
            ))}
        </Card>

        <Flex flexDirection="column" mx={24} mt={32} mb={8} gap={8}>
          <Typography variant="body-bold">
            Want to deploy your own Cloudflare Worker?
          </Typography>
          <Typography color="grays-mid">
            If you&apos;d prefer to manage your own worker configuration,
            documentation will be available soon.
          </Typography>
        </Flex>
      </Flex>

      {isModalOpen && (
        <CloudflareIntegrationModal
          isOpen
          isEditing={editingIntegration !== null}
          isDeployed={editingIntegration?.status === 'provisioned'}
          initialValues={
            editingIntegration
              ? {
                  name: editingIntegration.name || '',
                  account_id: editingIntegration.account_id,
                  zone_id: editingIntegration.zone_id,
                  route_pattern: editingIntegration.route_pattern,
                  deploy_robots_txt: editingIntegration.deploy_robots_txt,
                  api_token_masked: editingIntegration.api_token,
                }
              : undefined
          }
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleModalSubmit}
          serverError={serverError}
        />
      )}
    </Flex>
  );
};
